import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Loader2, MessageSquare, Send, Sparkles } from "lucide-react";
import { useEffect, useRef, useState } from "react";

import {
  decisions as decisionsApi,
  type ApiDecisionChatMessage,
} from "@/lib/api";

/**
 * Chat panel scoped to a single decision.
 *
 * DELIBERATELY not the global `ChatPanel` component — a decision chat has
 * different context (decision + workspace snapshot, not the workspace's chat
 * thread history) and different persistence (`decision_messages`, not
 * `chat_messages`). The visual difference — header copy, tint, empty state —
 * is what tells the operator this is a scoped conversation.
 */
export function DecisionChatPanel({
  workspaceId,
  decisionId,
}: {
  workspaceId: string;
  decisionId: string;
}) {
  const qc = useQueryClient();
  const [input, setInput] = useState("");
  const [timeoutError, setTimeoutError] = useState<string | null>(null);
  const scrollRef = useRef<HTMLDivElement | null>(null);

  const messagesQuery = useQuery({
    queryKey: ["decision-messages", workspaceId, decisionId],
    queryFn: () => decisionsApi.messages(workspaceId, decisionId),
  });

  const chat = useMutation({
    mutationFn: (message: string) =>
      decisionsApi.chat(workspaceId, decisionId, message),
    onSuccess: () => {
      setInput("");
      setTimeoutError(null);
      qc.invalidateQueries({
        queryKey: ["decision-messages", workspaceId, decisionId],
      });
    },
    onError: (e) => {
      const msg = e instanceof Error ? e.message : String(e);
      if (msg.toLowerCase().includes("timed out") || msg.includes("504")) {
        setTimeoutError(
          "The AI took longer than 30 seconds to reply. Try a shorter question, or retry.",
        );
      } else {
        setTimeoutError(msg);
      }
    },
  });

  const messages = messagesQuery.data ?? [];

  useEffect(() => {
    const el = scrollRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [messages.length, chat.isPending]);

  const submit = () => {
    const msg = input.trim();
    if (!msg || chat.isPending) return;
    chat.mutate(msg);
  };

  return (
    <div className="flex flex-col h-full min-h-0 border border-primary/30 rounded-lg bg-surface/50">
      {/* Header — deliberately distinct copy from the global Chat panel */}
      <div className="px-3 py-2 border-b border-primary/25 bg-primary/5">
        <div className="flex items-center gap-1.5 text-[11px] font-semibold tracking-[0.12em] uppercase text-primary">
          <MessageSquare className="h-3.5 w-3.5" />
          Discussion about this decision
        </div>
        <div className="text-[10.5px] text-muted-foreground mt-0.5">
          Scoped to this decision — not the workspace chat.
        </div>
      </div>

      {/* Messages */}
      <div
        ref={scrollRef}
        className="flex-1 min-h-0 overflow-y-auto scrollbar-thin p-3 space-y-3"
      >
        {messagesQuery.isLoading ? (
          <div className="text-center text-[11px] text-muted-foreground py-6">
            Loading messages…
          </div>
        ) : messages.length === 0 ? (
          <div className="text-center text-[11.5px] text-muted-foreground py-6 px-4">
            <Sparkles className="h-4 w-4 mx-auto mb-2 text-primary/60" />
            Ask a follow-up about this decision — e.g.,{" "}
            <span className="italic">"What would change your recommendation?"</span>
          </div>
        ) : (
          messages.map((m) => <ChatBubble key={m.id} message={m} />)
        )}
        {chat.isPending && (
          <div className="flex items-center gap-2 text-[11px] text-muted-foreground pl-1">
            <Loader2 className="h-3 w-3 animate-spin" />
            Thinking…
          </div>
        )}
      </div>

      {/* Error banner (explicit — never a generic toast) */}
      {timeoutError && (
        <div className="mx-3 mb-2 rounded-md border border-risk-medium/40 bg-risk-medium/10 px-3 py-2 text-[11.5px] text-risk-medium">
          <div className="font-semibold mb-0.5">Chat couldn't complete</div>
          <div className="text-muted-foreground">{timeoutError}</div>
          <button
            onClick={() => {
              const last = messages
                .slice()
                .reverse()
                .find((m) => m.role === "user");
              if (last) chat.mutate(last.content);
            }}
            className="mt-1.5 text-[11px] font-semibold text-risk-medium hover:underline"
          >
            Retry last message
          </button>
        </div>
      )}

      {/* Composer */}
      <div className="border-t border-primary/25 p-2 flex gap-2">
        <input
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && !e.shiftKey) {
              e.preventDefault();
              submit();
            }
          }}
          placeholder="Ask about this decision…"
          className="flex-1 rounded-md border border-border bg-surface px-2.5 py-1.5 text-[12.5px] focus:outline-none focus:ring-1 focus:ring-primary"
          disabled={chat.isPending}
        />
        <button
          onClick={submit}
          disabled={chat.isPending || !input.trim()}
          className="inline-flex items-center gap-1 rounded-md bg-primary text-primary-foreground px-2.5 py-1.5 text-[12px] font-semibold disabled:opacity-50 disabled:cursor-not-allowed hover:bg-primary/90"
        >
          <Send className="h-3.5 w-3.5" />
        </button>
      </div>
    </div>
  );
}

function ChatBubble({ message }: { message: ApiDecisionChatMessage }) {
  const isUser = message.role === "user";
  return (
    <div className={`flex ${isUser ? "justify-end" : "justify-start"}`}>
      <div
        className={`max-w-[92%] rounded-md px-2.5 py-1.5 text-[12.5px] leading-relaxed whitespace-pre-wrap ${
          isUser
            ? "bg-primary/15 text-foreground border border-primary/25"
            : "bg-surface-elevated text-foreground border border-border"
        }`}
      >
        {message.content}
      </div>
    </div>
  );
}
